import React, { useState } from 'react';
import { Send, ArrowUpCircle, CreditCard, Home, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { userAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const MoveMoney = ({ profile, fetchProfile }) => {
  const { toast } = useToast();
  const [zelleData, setZelleData] = useState({ recipient: '', amount: '', message: '' });
  const [billPayData, setBillPayData] = useState({ payeeName: '', accountNumber: '', amount: '', paymentDate: '' });
  const [depositData, setDepositData] = useState({ amount: '', checkNumber: '', frontImage: null, backImage: null });
  const [externalAccountData, setExternalAccountData] = useState({ bankName: '', accountType: '', routingNumber: '', accountNumber: '' });
  const [loading, setLoading] = useState(false);

  const handleZellePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userAPI.sendZellePayment(zelleData.recipient, parseFloat(zelleData.amount), zelleData.message);
      toast({ title: 'Success', description: 'Zelle payment sent successfully' });
      setZelleData({ recipient: '', amount: '', message: '' });
      fetchProfile();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Payment failed',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBillPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userAPI.scheduleBillPayment(
        billPayData.payeeName, 
        billPayData.accountNumber, 
        parseFloat(billPayData.amount), 
        billPayData.paymentDate
      );
      toast({ title: 'Success', description: 'Bill payment scheduled successfully' });
      setBillPayData({ payeeName: '', accountNumber: '', amount: '', paymentDate: '' });
      fetchProfile();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Payment failed',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleRemoteDeposit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const frontImageBase64 = await handleFileToBase64(depositData.frontImage);
      const backImageBase64 = await handleFileToBase64(depositData.backImage);
      
      await userAPI.depositCheck(
        depositData.checkNumber,
        parseFloat(depositData.amount),
        frontImageBase64,
        backImageBase64
      );
      toast({ title: 'Success', description: 'Check deposit processed successfully' });
      setDepositData({ amount: '', checkNumber: '', frontImage: null, backImage: null });
      fetchProfile();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Deposit failed',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLinkExternalAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userAPI.linkExternalAccount(
        externalAccountData.bankName,
        externalAccountData.accountType,
        externalAccountData.routingNumber,
        externalAccountData.accountNumber
      );
      toast({ title: 'Success', description: 'External account linked successfully' });
      setExternalAccountData({ bankName: '', accountType: '', routingNumber: '', accountNumber: '' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to link account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Zelle */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
            <CardTitle className="flex items-center">
              <Send className="w-5 h-5 mr-2" />
              Zelle
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-gray-600 mb-4">Send money instantly to friends and family with Zelle.</p>
            <form onSubmit={handleZellePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Email or Phone *
                </label>
                <Input 
                  placeholder="email@example.com or (555) 123-4567" 
                  value={zelleData.recipient}
                  onChange={(e) => setZelleData({...zelleData, recipient: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount *
                </label>
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="0.00" 
                  value={zelleData.amount}
                  onChange={(e) => setZelleData({...zelleData, amount: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (Optional)
                </label>
                <Input 
                  placeholder="What's this for?" 
                  value={zelleData.message}
                  onChange={(e) => setZelleData({...zelleData, message: e.target.value})}
                />
              </div>
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={loading}>
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Send with Zelle'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Bill Pay */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white">
            <CardTitle className="flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              Bill Pay
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-gray-600 mb-4">Pay your bills securely and on time.</p>
            <form onSubmit={handleBillPayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payee Name *
                </label>
                <Input 
                  placeholder="Electric Company, etc." 
                  value={billPayData.payeeName}
                  onChange={(e) => setBillPayData({...billPayData, payeeName: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number *
                </label>
                <Input 
                  placeholder="Account or reference number" 
                  value={billPayData.accountNumber}
                  onChange={(e) => setBillPayData({...billPayData, accountNumber: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount *
                </label>
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="0.00" 
                  value={billPayData.amount}
                  onChange={(e) => setBillPayData({...billPayData, amount: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date *
                </label>
                <Input 
                  type="date" 
                  value={billPayData.paymentDate}
                  onChange={(e) => setBillPayData({...billPayData, paymentDate: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={loading}>
                <CreditCard className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Schedule Payment'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Remote Deposit */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardTitle className="flex items-center">
              <ArrowUpCircle className="w-5 h-5 mr-2" />
              Remote Deposit
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-gray-600 mb-4">Deposit checks remotely using your device camera.</p>
            <form onSubmit={handleRemoteDeposit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Check Number *
                </label>
                <Input 
                  placeholder="Check number" 
                  value={depositData.checkNumber}
                  onChange={(e) => setDepositData({...depositData, checkNumber: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Check Amount *
                </label>
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="0.00" 
                  value={depositData.amount}
                  onChange={(e) => setDepositData({...depositData, amount: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Front of Check *
                </label>
                <Input 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => setDepositData({...depositData, frontImage: e.target.files[0]})}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Upload or take photo of check front</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Back of Check *
                </label>
                <Input 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => setDepositData({...depositData, backImage: e.target.files[0]})}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Upload or take photo of endorsed back</p>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                <ArrowUpCircle className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Submit Deposit'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Add External Bank Account */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="bg-gradient-to-r from-orange-600 to-orange-700 text-white">
            <CardTitle className="flex items-center">
              <Home className="w-5 h-5 mr-2" />
              Add External Bank Account
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-gray-600 mb-4">Link an external bank account for transfers.</p>
            <form onSubmit={handleLinkExternalAccount} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name *
                </label>
                <Input 
                  placeholder="Chase, Bank of America, etc." 
                  value={externalAccountData.bankName}
                  onChange={(e) => setExternalAccountData({...externalAccountData, bankName: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Type *
                </label>
                <Select 
                  value={externalAccountData.accountType}
                  onValueChange={(value) => setExternalAccountData({...externalAccountData, accountType: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Checking</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Routing Number *
                </label>
                <Input 
                  placeholder="9-digit routing number" 
                  maxLength={9} 
                  value={externalAccountData.routingNumber}
                  onChange={(e) => setExternalAccountData({...externalAccountData, routingNumber: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number *
                </label>
                <Input 
                  placeholder="Account number" 
                  value={externalAccountData.accountNumber}
                  onChange={(e) => setExternalAccountData({...externalAccountData, accountNumber: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700" disabled={loading}>
                <Home className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Link Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <MessageSquare className="w-5 h-5 text-blue-600 mt-1" />
            <div>
              <p className="font-medium text-blue-900">Need Help?</p>
              <p className="text-sm text-blue-700 mt-1">
                All transactions are processed securely. You will receive immediate confirmation for each transaction.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MoveMoney;
